import type { Breadcrumb } from '~/apps/search/schemas'
import type { SearchFilters } from '~/types/search'
import type { FacetRemovalRule } from '~/utility/facets'
import type { SimpleBreadcrumbWithCount } from '~/components/search/types'

export interface ComputeBreadcrumbsOptions {
  breadcrumbs: Breadcrumb[]
  counts?: Record<string, number>
  searchFilters: SearchFilters
  facetRules: FacetRemovalRule[] | null
  hierarchyCounts: Record<string, number>
  isGlampingCategory: boolean
  isDatedSearch: boolean
  getBaseUrl: (useSearch: boolean) => string
}

/**
 * Pure function to compute breadcrumb display data from raw breadcrumb input.
 *
 * This function is framework-agnostic and contains all the business logic for:
 * - Determining which breadcrumbs to show
 * - Computing breadcrumb URLs (SEO vs non-SEO)
 * - Handling facet tier insertion
 * - Managing facet query parameter removal
 *
 * @param options - All inputs needed for breadcrumb computation
 * @returns Array of breadcrumbs with computed display properties
 */
export function computeBreadcrumbs(
  options: ComputeBreadcrumbsOptions,
): SimpleBreadcrumbWithCount[] {
  const {
    breadcrumbs,
    counts,
    searchFilters,
    facetRules,
    hierarchyCounts,
    isGlampingCategory,
    isDatedSearch,
    getBaseUrl,
  } = options

  const newBreadcrumbs: SimpleBreadcrumbWithCount[] = []
  let removedFacetBreadcrumb: Breadcrumb | null = null
  const hasFacets = (searchFilters.facets ?? []).length > 0

  breadcrumbs.forEach((breadcrumb, index) => {
    if (breadcrumb.name === 'Pitchup.com') {
      return
    }

    const hierarchyCount = breadcrumb.hierarchyPath
      ? hierarchyCounts[breadcrumb.hierarchyPath]
      : undefined

    const count = breadcrumb.hierarchyPath
      ? counts?.[breadcrumb.hierarchyPath]
      : undefined

    const countLevel = hasFacets ? 5 : 1
    const shouldRemove = facetRules?.some((rule) => {
      if (rule.category_all) {
        return true
      }
      if (rule.category_campsites && !searchFilters.categoryIds?.length) {
        return true
      }

      // FIXED: For glamping pages, only apply glamping-specific rules
      if (isGlampingCategory) {
        if (rule.category_glamping) {
          return true
        }
        // Skip individual category rules for glamping pages
        return false
      }

      // For non-glamping pages, check individual category matches
      const currentCategoryIds = searchFilters.categoryIds || []
      const categoryMatch = currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
      if (categoryMatch) {
        return true
      }
      return false
    })

    // Check early return condition (facet breadcrumb at index 2)
    const shouldEarlyReturn = hasFacets && shouldRemove && index === 2

    if (shouldEarlyReturn) {
      // Track the removed facet breadcrumb so we can add it back later with correct URL
      removedFacetBreadcrumb = breadcrumb
      return
    }
    const linkable =
      typeof hierarchyCount === 'undefined' ||
      hierarchyCount >= countLevel ||
      typeof count === 'undefined'

    // FIXED: Category root (index 1) should always use SEO URL, not /search/
    // It should just remove the facet parameter via removeFacetQuery
    const isCategoryRoot = index === 1
    const shouldUseSeoUrl = isCategoryRoot || (!shouldRemove && !isDatedSearch)

    const url =
      breadcrumb.hierarchyPath === '/'
        ? shouldUseSeoUrl
          ? breadcrumb.url
          : `${getBaseUrl(true)}/`
        : `${getBaseUrl(Boolean(shouldRemove) || isDatedSearch)}/${breadcrumb.hierarchyPath}`

    // FIXED: Always remove facet query from category root (index 1) when facets present
    // The facet now has its own tier in the breadcrumb, so the category root should never have ?facet=
    let removeFacetQuery = false
    if (index === 1 && hasFacets) {
      removeFacetQuery = true
    }

    // Category root should not be marked as nonSeo even when facets present
    const isNonSeo = isCategoryRoot
      ? isDatedSearch
      : Boolean(shouldRemove) || isDatedSearch

    newBreadcrumbs.push({
      label: breadcrumb.name,
      url: url,
      count,
      hierarchyLevel: breadcrumb.hierarchyLevel ?? undefined,
      hierarchyPath: breadcrumb.hierarchyPath ?? undefined,
      linkable,
      nonSeo: isNonSeo,
      to: { path: url },
      removeFacetQuery,
      debugData: {
        hierarchyCount,
        linkable,
        nonSeo: isNonSeo,
        hierarchyPath: breadcrumb.hierarchyPath,
        level: breadcrumb.hierarchyLevel,
      },
    })
  })

  // Add back the facet tier if it was removed
  if (removedFacetBreadcrumb && searchFilters.facets?.length === 1) {
    const facetSlug = searchFilters.facets[0]

    // FIXED: Determine if we should use SEO URL or query param
    // Use ?facet= if the facet is removed for the current category (same logic as shouldRemove)
    const useQueryParam = facetRules?.some((rule) => {
      console.log(
        '[BREADCRUMB_DEBUG_MJ2025] Checking facet rule for facet tier:',
        {
          facet: facetSlug,
          rule,
          isGlampingCategory,
          categoryIds: searchFilters.categoryIds,
        },
      )

      if (rule.category_all) {
        console.log(
          '[BREADCRUMB_DEBUG_MJ2025] Using query param: category_all is true',
        )
        return true
      }
      if (rule.category_campsites && !searchFilters.categoryIds?.length) {
        console.log(
          '[BREADCRUMB_DEBUG_MJ2025] Using query param: category_campsites and no categoryIds',
        )
        return true
      }

      // For glamping pages, only apply glamping-specific rules
      if (isGlampingCategory) {
        if (rule.category_glamping) {
          console.log(
            '[BREADCRUMB_DEBUG_MJ2025] Using query param: category_glamping',
          )
          return true
        }
        // Skip individual category rules for glamping pages
        return false
      }

      // For non-glamping pages, check individual category matches
      const currentCategoryIds = searchFilters.categoryIds || []
      const categoryMatch = currentCategoryIds.some((id) =>
        rule.category_ids.includes(Number(id)),
      )
      if (categoryMatch) {
        console.log(
          '[BREADCRUMB_DEBUG_MJ2025] Using query param: category_ids match',
          {
            currentCategoryIds,
            ruleCategoryIds: rule.category_ids,
          },
        )
        return true
      }
      return false
    })

    console.log('[BREADCRUMB_DEBUG_MJ2025] Facet tier decision:', {
      facetSlug,
      useQueryParam,
      isGlampingCategory,
      categoryIds: searchFilters.categoryIds,
      facetRulesCount: facetRules?.length,
    })

    // Build the facet breadcrumb URL
    // FIXED: Facet tier should only link to base search URL, not include hierarchy
    const facetUrl = useQueryParam
      ? `${getBaseUrl(true)}/` // Base search URL, ?facet= added via query in SearchBreadcrumbsGenerator
      : `${getBaseUrl(false)}/-/${facetSlug}/`

    // FIXED: Insert facet tier after category root (position 1), not before last hierarchy item
    // Example: Home › Campsites › [INSERT FACET HERE] › England › South West › Devon
    const facetBreadcrumb = {
      label: removedFacetBreadcrumb.name,
      url: facetUrl,
      count: undefined,
      hierarchyLevel: undefined,
      hierarchyPath: '/',
      linkable: true,
      nonSeo: useQueryParam,
      to: { path: facetUrl },
      removeFacetQuery: false,
      debugData: {
        hierarchyCount: undefined,
        linkable: true,
        nonSeo: useQueryParam,
        hierarchyPath: '/',
        level: undefined,
      },
    }

    // Insert after category root (index 0 is category root, insert at position 1)
    newBreadcrumbs.splice(1, 0, facetBreadcrumb)

    console.log('[BREADCRUMB_DEBUG_MJ2025] Inserted facet at position 1:', {
      breadcrumbOrder: newBreadcrumbs.map((b) => b.label),
    })
  }

  return newBreadcrumbs
}
